import { takeEvery, call, put, delay } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { getApi, getDirectApi } from './index'
import { callApi } from './functions'
import { withOfflineAccess } from '../lockapp/offlineAccess'
import {
	fetchAppMetaData,
	LoadAppMetaDataFromNetworkParams as fetchAppMetaDataAction,
	LoadAppMetaDataFromNetworkResultFailure,
	fetchCollectionItems,
	LoadCollectionItemsFromNetworkParams as fetchCollectionItemsAction,
	fetchCollection,
	LoadCollectionFromNetworkParams as fetchCollectionAction,
	fetchBundle,
	LoadBundleFromNetworkParams as fetchBundleAction,
	BundleRequestParams,
	fetchScreen,
	LoadScreenFromNetworkParams as fetchScreenAction,
	fetchMapRoute,
	LoadMapRouteFromNetworkParams as fetchMapRouteAction,
	RequestParams,
	CollectionItemsRequestParams,
	CollectionRequestParams,
	ScreenRequestParams,
	validateRentalToken,
	ValidateRentalTokenParams,
	RentalTokenParams,
	fetchManifest,
	LoadManifestFromNetworkParams,
	ManifestRequestParams,
	LoadMediaItemFromNetworkParams,
	MediaItemRequestParams,
	fetchMediaItem,
	fetchForm,
	MapRouteRequestParams,
	validateCode,
	redeemCode,
	LoadCouponCodeParams as loadCouponCodeAction,
	CouponCodeRequestParams,
	fetchManifestComplete,
	ValidateCodeAction,
	ValidateCodeParams,
	LoadFormFromNetworkParams,
	FormRequestParams,
	redeemBundleCoupon,
	validateBundleCoupon,
	fetchInAppPurchases,
	RedeemBundleCouponParams,
	RedeemBundleCouponPayload,
	validateOwnedCollection,
	ValidateRedeemedCodePayload,
	ValidateRedeemedCodeResultSuccess,
	ValidateRedeemedCodeResultFailure,
	ValidateRedeemedCodeActionCreator,
	FetchInAppPurchasesParams,
	InAppPurchasesRequestParams,
} from './actions'
import { isHttpClientError } from './errors'
import { Action } from 'typescript-fsa'
import {
	ValidateToken,
	GetMediaItemsShowResponse,
	Manifest,
	MapRoute,
	RedeemCodeResponse,
	RedeemCode,
	BundleItem,
	BundleFull,
} from 'mytours-read-api-typescript'
import { Collection, Screen, CollectionItems } from './types'
import { getConfiguration } from '../configuration'
import { store } from '../root/index'
import { getCurrentLanguageCode } from '../settings/functions'

/**
 * Fetches app meta data from api. App id is specified by Auth username param (set in Api configuration).
 * @param action contains request parameters (devicePlatform and deviceUdid)
 */
function* handleFetchAppMetaData(action: fetchAppMetaDataAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchAppMetaData, (payload: RequestParams, options: RequestInit) => {
		const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
		if (shouldUsePublishedContent) {
			return getConfiguration().stqryBunnyReadApi.fetchApp(getConfiguration().getAccountId())
		} else {
			const { devicePlatform, deviceUdid } = payload
			return getDirectApi().appShow(devicePlatform, deviceUdid, options)
		}
	})
}

function* handleFetchAppMetaDataFailed(action: LoadAppMetaDataFromNetworkResultFailure): SagaIterator {
	const {
		payload: {
			error,
			params: { onError },
		},
	} = action
	onError?.(error)
}

const DEMO_VALID_CODE = 'VALID'

function* handleValidateCode(action: ValidateCodeAction): SagaIterator {
	if (action.payload.expireAt) {
		const status =
			action.payload.expireAt > Date.now() ? RedeemCodeResponse.StatusEnum.Valid : RedeemCodeResponse.StatusEnum.Expired
		yield delay(0)
		yield delay(0)
		// two immediate delays needed for the component to get into the right state
		yield put(validateCode.done({ params: action.payload, result: { status: status } }))
		return
	}

	if (getConfiguration().isDemoMode() && action.payload.couponCode?.toUpperCase() === DEMO_VALID_CODE) {
		yield delay(0)
		yield delay(0)
		yield put(validateCode.done({ params: action.payload, result: { status: RedeemCodeResponse.StatusEnum.Valid } }))
		return
	}

	// @ts-ignore
	yield call(
		callApi,
		action,
		validateCode,
		withOfflineAccess((payload: ValidateCodeParams, options: RequestInit) => {
			const { devicePlatform, deviceUdid, couponCode, linkedId, linkedType } = payload
			return getDirectApi().validateCode(
				devicePlatform,
				deviceUdid,
				couponCode,
				linkedId,
				linkedType,
				undefined,
				options,
			)
		}),
	)
}

/**
 * Re-validates a redeemed code (app start / metadata sync / screen focus). Honours an expireAt
 * short-circuit and the demo code, otherwise validates against the backend. Shared by the bundle
 * and owned-collection revalidation flows, which differ only in linked type and target action.
 */
function* handleValidateRedeemedCode(
	asyncAction: ValidateRedeemedCodeActionCreator,
	linkedType: RedeemCode.LinkedTypeEnum,
	action: Action<ValidateRedeemedCodePayload>,
): SagaIterator {
	if (action.payload.expireAt) {
		const status =
			action.payload.expireAt > Date.now() ? RedeemCodeResponse.StatusEnum.Valid : RedeemCodeResponse.StatusEnum.Expired
		yield delay(0)
		yield delay(0)
		yield put(asyncAction.done({ params: action.payload, result: { status } }))
		return
	}

	if (getConfiguration().isDemoMode() && action.payload.coupon?.toUpperCase() === DEMO_VALID_CODE) {
		yield delay(0)
		yield delay(0)
		yield put(asyncAction.done({ params: action.payload, result: { status: RedeemCodeResponse.StatusEnum.Valid } }))
		return
	}

	// @ts-ignore
	yield call(
		callApi,
		action,
		asyncAction,
		withOfflineAccess(
			(payload: ValidateRedeemedCodePayload, options: RequestInit) => {
				const deviceUdid = getConfiguration().getDeviceUUID()
				return getDirectApi().validateCode(
					payload.devicePlatform,
					deviceUdid,
					payload.coupon,
					payload.id,
					linkedType,
					undefined,
					options,
				)
			},
			// This payload names its target `coupon`/`id`, so it is restated in the shape a stored
			// redemption is matched by.
			payload => ({ couponCode: payload.coupon, linkedId: payload.id, linkedType }),
		),
	)
}

function* handleRedeemCode(action: loadCouponCodeAction): SagaIterator {
	if (getConfiguration().isDemoMode() && action.payload.couponCode?.toUpperCase() === DEMO_VALID_CODE) {
		const result: RedeemCodeResponse = { status: RedeemCodeResponse.StatusEnum.Valid, expire_after: 24 }
		yield delay(0)
		yield delay(0)
		yield put(redeemCode.done({ params: action.payload, result }))
		return
	}

	// @ts-ignore
	yield call(callApi, action, redeemCode, async (payload: CouponCodeRequestParams, options: RequestInit) => {
		const { devicePlatform, deviceUdid, couponCode, linkedId, linkedType, email } = payload
		const state = store.getState()
		const response = await getDirectApi().validateCode(
			devicePlatform,
			deviceUdid,
			couponCode,
			linkedId,
			linkedType,
			email,
			options,
		)
		if (response.status !== RedeemCodeResponse.StatusEnum.Valid) {
			return response
		}

		return getDirectApi(response.region).redeemCode(
			devicePlatform,
			deviceUdid,
			{
				coupon_code: couponCode,
				linked_id: linkedId,
				linked_type: linkedType,
				email,
				language_code: getCurrentLanguageCode(state),
			},
			options,
		)
	})
}

/**
 * Reads the cached IAP catalogue from disk and returns the items of the bundle with `bundleId`.
 * Used so the redeem flow can show the bundle's contents immediately, without an extra round-trip.
 */
async function loadBundleItemsFromDisk(bundleId: number): Promise<BundleItem[] | undefined> {
	try {
		const state = store.getState()
		const appId = state.settings.appInfo.appId
		const updatedAt = state.settings.appMetaData?.updated_at
		if (!appId || !updatedAt) {
			return undefined
		}
		const iaps = await getConfiguration().loadInAppPurchasesDataFromDisk(appId, updatedAt.toString())
		const match = iaps?.find(iap => iap.bundle?.id === bundleId)
		return match?.bundle?.items
	} catch (error) {
		console.warn('loadBundleItemsFromDisk failed', error)
		return undefined
	}
}

/**
 * Redeems a coupon to unlock a bundle: validate -> redeem (linked_type=Bundle) -> attach the
 * bundle's items from the cached IAP catalogue so the contents view renders immediately.
 */
function* handleRedeemBundleCoupon(action: RedeemBundleCouponParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, redeemBundleCoupon, async (payload: RedeemBundleCouponPayload, options: RequestInit) => {
		const deviceUdid = getConfiguration().getDeviceUUID()
		if (getConfiguration().isDemoMode() && payload.coupon?.toUpperCase() === DEMO_VALID_CODE) {
			const items = await loadBundleItemsFromDisk(payload.id)
			return { status: RedeemCodeResponse.StatusEnum.Valid, expire_after: 24, items }
		}

		const response = await getDirectApi().validateCode(
			payload.devicePlatform,
			deviceUdid,
			payload.coupon,
			payload.id,
			RedeemCode.LinkedTypeEnum.Bundle,
			payload.email,
			options,
		)
		if (response.status !== RedeemCodeResponse.StatusEnum.Valid) {
			return response
		}

		const state = store.getState()
		const redeemResponse = await getDirectApi(response.region).redeemCode(
			payload.devicePlatform,
			deviceUdid,
			{
				coupon_code: payload.coupon,
				email: payload.email,
				linked_id: payload.id,
				linked_type: RedeemCode.LinkedTypeEnum.Bundle,
				language_code: getCurrentLanguageCode(state),
			},
			options,
		)
		if (redeemResponse.status !== RedeemCodeResponse.StatusEnum.Valid) {
			return redeemResponse
		}

		const items = await loadBundleItemsFromDisk(payload.id)
		return { ...redeemResponse, items }
	})
}

function* removeCodeFromDisk(linkedType: RedeemCode.LinkedTypeEnum, coupon: string, id: number): SagaIterator {
	try {
		const projectId = getConfiguration().getDefaultAppInfo().projectId
		yield call(getConfiguration().removeCouponCodeFromDisk, projectId, {
			code: coupon,
			linkedId: id,
			linkedType,
		})
	} catch (error) {
		// Error deleting data
	}
}

/**
 * On a definitive Invalid/Expired re-validation result, drop the persisted code so a
 * revoked/expired unlock isn't rebuilt on the next boot. Shared by the bundles and
 * collectionownership sagas.
 */
export function* removeRevokedCodeFromDisk(
	linkedType: RedeemCode.LinkedTypeEnum,
	action: ValidateRedeemedCodeResultSuccess,
): SagaIterator {
	const { coupon, id } = action.payload.params
	const { status } = action.payload.result
	if (status === RedeemCodeResponse.StatusEnum.Invalid || status === RedeemCodeResponse.StatusEnum.Expired) {
		yield call(removeCodeFromDisk, linkedType, coupon, id)
	}
}

/**
 * The `.failed` counterpart of removeRevokedCodeFromDisk: a 4xx response is also a definitive
 * rejection (e.g. the code or its target was deleted → 404/410), so drop the persisted code to
 * keep disk consistent with the reducers, which drop the slice entry on the same condition.
 * Anything else — transport failure or 5xx — is transient and must NOT remove the code (the
 * reducers keep the optimistic offline unlock).
 */
export function* removeRejectedCodeFromDisk(
	linkedType: RedeemCode.LinkedTypeEnum,
	action: ValidateRedeemedCodeResultFailure,
): SagaIterator {
	if (isHttpClientError(action.payload.error)) {
		const { coupon, id } = action.payload.params
		yield call(removeCodeFromDisk, linkedType, coupon, id)
	}
}

/**
 * Re-dispatch a validation for every persisted code of `linkedType`, rebuilding the matching
 * (persistence-blacklisted) slice from disk. Shared by the bundles and collectionownership
 * metadata-ready handlers.
 */
export function* revalidatePersistedCodes(
	linkedType: RedeemCode.LinkedTypeEnum,
	asyncAction: ValidateRedeemedCodeActionCreator,
): SagaIterator {
	const devicePlatform = getConfiguration().getPlatform()
	const projectId = getConfiguration().getDefaultAppInfo().projectId
	const codes = yield call(getConfiguration().loadCouponCodesFromDisk, projectId)
	for (const code of codes || []) {
		if (code?.linkedType === linkedType) {
			yield put(
				asyncAction.started({
					devicePlatform,
					coupon: code.code,
					id: code.linkedId,
					expireAt: code.expireAt,
					addedAt: code.addedAt,
				}),
			)
		}
	}
}

/**
 * Fetches the IAP catalogue and caches it to disk so a bundle's items can be resolved during
 * redemption (loadBundleItemsFromDisk) and reconciled on the bundles slice (fetchInAppPurchases.done).
 */
function* handleFetchInAppPurchases(action: FetchInAppPurchasesParams): SagaIterator {
	// @ts-ignore
	yield call(
		callApi,
		action,
		fetchInAppPurchases,
		async (payload: InAppPurchasesRequestParams, options: RequestInit) => {
			const deviceUdid = getConfiguration().getDeviceUUID()
			const response = await getDirectApi().inAppPurchasesIndex(
				payload.devicePlatform,
				payload.version,
				payload.language,
				deviceUdid,
				1,
				1000,
				options,
			)
			try {
				const appId = store.getState().settings.appInfo.appId
				if (appId && response.data && response.data.length > 0) {
					await getConfiguration().saveInAppPurchasesToDisk(appId, payload.version.toString(), response.data)
				}
			} catch (error) {
				console.warn('saveInAppPurchasesToDisk failed', error)
			}
			return response
		},
	)
}

/**
 * Fetches collection items from api. App id is specified by Auth username param (set in Api configuration).
 * @param action contains request parameters
 */
function* handleFetchCollectionItems(action: fetchCollectionItemsAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchCollectionItems, (payload: CollectionItemsRequestParams, options: RequestInit) => {
		return downloadCollectionItemsFromAPI(payload)
	})
}

export function downloadCollectionItemsFromAPI(payload: CollectionItemsRequestParams): Promise<CollectionItems> {
	const { devicePlatform, collectionId, collectionVersion, accountId, language, deviceUdid, full } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		const owner = accountId || getConfiguration().getAccountId()
		// A single page collection publishes its items with their screens inline, in the same shape the
		// direct API serves from `?full=true`; the items index it publishes alongside carries no screen.
		if (full) {
			return getConfiguration()
				.stqryBunnyReadApi.fetchCollection(owner, collectionId, collectionVersion, language)
				.then(collection => ({ data: collection.items }))
		}

		return getConfiguration().stqryBunnyReadApi.fetchCollectionItems(owner, collectionId, collectionVersion, language)
	} else if (full) {
		// The collection endpoint is the only one that returns each item's screen with it, which is what
		// spares a single page tour a request per stop.
		return getApi()
			.collectionsShow(devicePlatform, collectionId, collectionVersion, language, deviceUdid, 'true')
			.then(collection => ({ data: collection.items }))
	} else {
		// making default page size really big until we think about adding pagination (tour stops need to download full stop count though)
		return getApi().collectionItemsIndex(
			devicePlatform,
			collectionId,
			collectionVersion,
			language,
			deviceUdid,
			undefined,
			1000,
		)
	}
}

function* handleFetchCollection(action: fetchCollectionAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchCollection, (payload: CollectionRequestParams, options: RequestInit) => {
		return downloadCollectionDetailsFromAPI(payload)
	})
}

export function downloadCollectionDetailsFromAPI(payload: CollectionRequestParams): Promise<Collection> {
	const { devicePlatform, collectionId, collectionVersion, accountId, language, deviceUdid } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchCollection(
			accountId || getConfiguration().getAccountId(),
			collectionId,
			collectionVersion,
			language,
		)
	} else {
		return getApi().collectionsShow(devicePlatform, collectionId, collectionVersion, language, deviceUdid)
	}
}

/**
 * Fetches a bundle so a collection it gates can offer a way in.
 *
 * The IAP catalogue would answer this for a same-region app, but it cannot reach a guide client in another
 * region — that content is only ever published — so the bundle is fetched the way the collection holding it
 * was, under the account the payload names rather than the app's.
 */
function* handleFetchBundle(action: fetchBundleAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchBundle, (payload: BundleRequestParams) => downloadBundleFromAPI(payload))
}

export function downloadBundleFromAPI(payload: BundleRequestParams): Promise<BundleFull> {
	const { devicePlatform, bundleId, bundleVersion, accountId, language, deviceUdid } = payload
	if (getConfiguration().shouldUsePublishedContent()) {
		return getConfiguration().stqryBunnyReadApi.fetchBundle(
			accountId || getConfiguration().getAccountId(),
			bundleId,
			bundleVersion,
			language,
		)
	}
	return getApi().bundlesShow(devicePlatform, bundleId, bundleVersion, language, deviceUdid)
}

/**
 * Fetches a single screen from api.
 * @param action contains request parameters
 */
function* handleFetchScreen(action: fetchScreenAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchScreen, (payload: ScreenRequestParams, options: RequestInit) => {
		return downloadScreenFromAPI(payload)
	})
}

export function downloadScreenFromAPI(params: ScreenRequestParams): Promise<Screen> {
	const { devicePlatform, screenId, screenVersion, accountId, language, deviceUdid } = params
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchScreen(
			accountId || getConfiguration().getAccountId(),
			screenId,
			screenVersion,
			language,
		)
	} else {
		return getApi().screensShow(devicePlatform, screenId, screenVersion, language, deviceUdid)
	}
}

/**
 * Fetches a single media item from api.
 * @param action contains request parameters
 */
function* handleFetchMediaItem(action: LoadMediaItemFromNetworkParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchMediaItem, (payload: MediaItemRequestParams, options: RequestInit) => {
		return downloadMediaItemFromAPI(payload)
	})
}

/**
 * Downloads the media item json file from network. Exported function because we only use it for the Sync Content feature and do not need to persist media json files to disk,
 * only to extract the required information to download the actual media files.
 * @param params the params to perform the request
 */
export function downloadMediaItemFromAPI(params: MediaItemRequestParams): Promise<GetMediaItemsShowResponse> {
	const { devicePlatform, itemId, itemVersion, language, deviceUdid } = params
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchMediaItem(
			getConfiguration().getAccountId(),
			itemId,
			itemVersion,
			language,
		)
	} else {
		return getApi().mediaItemsShow(devicePlatform, itemId, itemVersion, language, deviceUdid)
	}
}

/**
 * Fetches a single form from api.
 * @param action contains request parameters
 */
function* handleFetchForm(action: LoadFormFromNetworkParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchForm, (payload: FormRequestParams, options: RequestInit) => {
		const { id, version, language } = payload
		const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
		if (shouldUsePublishedContent) {
			return getConfiguration().stqryBunnyReadApi.fetchForm(getConfiguration().getAccountId(), id, version, language)
		} else {
			return getApi().formShow(id, version, language)
		}
	})
}

/**
 * Validates a short rental token and exchanges it for an app id and app token to be used to setup rental app
 * @param action contains request parameters
 */
function* handleValidateRentalToken(action: ValidateRentalTokenParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, validateRentalToken, (payload: RentalTokenParams, options: RequestInit) => {
		const { appId, token } = payload
		const params: ValidateToken = { short_token: token, token_type: ValidateToken.TokenTypeEnum.RentalMode }
		return getDirectApi().appToken(appId, params)
	})
}

/**
 * Validates a short rental token and exchanges it for an app id and app token to be used to setup rental app
 * @param action contains request parameters
 */
function* handleFetchManifest(action: LoadManifestFromNetworkParams): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	// check if exists on Disk
	const manifest: Manifest | undefined = yield call(getConfiguration().loadAppManifestFromDisk, projectId)
	if (manifest) {
		// store onto redux state
		yield put(fetchManifestComplete(manifest))
	} else {
		// @ts-ignore
		yield call(callApi, action, fetchManifest, (payload: ManifestRequestParams, options: RequestInit) => {
			const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
			if (shouldUsePublishedContent) {
				return getConfiguration().stqryBunnyReadApi.fetchManifest(getConfiguration().getAccountId())
			} else {
				return getDirectApi().appManifest(payload.devicePlatform, payload.deviceUdid)
			}
		})
	}
}

/**
 * Fetches map toutes from api.
 * @param action contains request parameters
 */
function* handleFetchMapRoute(action: fetchMapRouteAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchMapRoute, (payload: MapRouteRequestParams, options: RequestInit) => {
		return downloadMapRouteFromAPI(payload)
	})
}

export function downloadMapRouteFromAPI(payload: MapRouteRequestParams): Promise<MapRoute> {
	const { devicePlatform, mapRouteVersion, language, mapRouteId, deviceUdid, accountId } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchMapRoute(
			accountId || getConfiguration().getAccountId(),
			mapRouteId,
			mapRouteVersion,
			language,
		)
	} else {
		return getApi().mapRouteShow(devicePlatform, mapRouteVersion, language, mapRouteId, deviceUdid)
	}
}

export default function* (): SagaIterator {
	// loads a single collection item (action contains item id)
	yield takeEvery(fetchScreen.started, handleFetchScreen)

	// loads a single collection (action contains collection id and version number)
	yield takeEvery(fetchCollectionItems.started, handleFetchCollectionItems)
	yield takeEvery(fetchCollection.started, handleFetchCollection)
	yield takeEvery(fetchBundle.started, handleFetchBundle)

	yield takeEvery(fetchMediaItem.started, handleFetchMediaItem)

	yield takeEvery(fetchForm.started, handleFetchForm)

	yield takeEvery(validateRentalToken.started, handleValidateRentalToken)
	yield takeEvery(fetchManifest.started, handleFetchManifest)

	yield takeEvery(fetchMapRoute.started, handleFetchMapRoute)

	// loads meta data for a given app id
	yield takeEvery(fetchAppMetaData.started, handleFetchAppMetaData)
	yield takeEvery(fetchAppMetaData.failed, handleFetchAppMetaDataFailed)

	yield takeEvery(validateCode.started, handleValidateCode)
	yield takeEvery(
		validateBundleCoupon.started,
		handleValidateRedeemedCode,
		validateBundleCoupon,
		RedeemCode.LinkedTypeEnum.Bundle,
	)
	yield takeEvery(redeemCode.started, handleRedeemCode)

	yield takeEvery(redeemBundleCoupon.started, handleRedeemBundleCoupon)
	yield takeEvery(
		validateOwnedCollection.started,
		handleValidateRedeemedCode,
		validateOwnedCollection,
		RedeemCode.LinkedTypeEnum.Collection,
	)
	yield takeEvery(fetchInAppPurchases.started, handleFetchInAppPurchases)
}
